import _ from "lodash";

export const CROP_YEAR_POTENTIAL = "potential";

export const BASE_ACRES_COMMODITY_NAMES = [
  "Barley",
  "Beans-Large Chickpeas",
  "Beans-Small Chickpeas",
  "Canola",
  "Corn",
  "Cotton",
  "Crambe",
  "Dry Peas",
  "Flaxseed",
  "Grain Sorghum",
  "Lentils",
  "Mustard Seed",
  "Oats",
  "Peanuts",
  "Rapeseed",
  "Rice",
  "Safflower",
  "Sesame Seed",
  "Soybeans",
  "Sunflower Seed",
  "Triticale",
  "Wheat",
];

export const BASE_ACRES_COMMODITY_NAMES_WITH_TYPE = ["Rice"];

/**
 * CCC-866 / source-document style for Rice rows in commodity columns (e.g. `13. Commodity`):
 * `RICE-LONG GRAIN`, `RICE-MEDIUM GRAIN`, not title-case `Rice Long Grain`.
 */
export function formatBaseAcresRiceExportCommodityLabel(type_name) {
  if (!type_name || !String(type_name).trim()) {
    return "RICE";
  }
  const typeUpper = String(type_name).trim().replace(/\s+/g, " ").toUpperCase();
  return `RICE-${typeUpper}`;
}

function readSharePercentRaw(row) {
  if (!row || typeof row !== "object") return null;
  const v = row.share_percent ?? row.SharePercent;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * CCC-866 payment share is farm-level; API rows often only populate `share_percent` on some
 * commodities while tract rows show 0. For export, align with the form: repeat the effective
 * share on every row per `{fsa_farm_number, crop_year}`.
 *
 * - Within each group, take the **maximum** positive share (handles partial shares like 0.7293).
 * - Rows with share `null`, missing, or **0** are filled from that value.
 * - If every row in the group is empty/zero, use **1** (100% decimal) so the sheet matches typical sole-operator PDFs.
 */
export function coalesceBaseAcresExportSharePercent(rows) {
  if (!rows?.length) return rows;
  const keyOf = (r) => {
    const farm = r.fsa_farm_number ?? r.FsaFarmNumber ?? "";
    const y = r.crop_year ?? r.CropYear ?? r.cropYear ?? "";
    return `${farm}|${y}`;
  };
  const byKey = new Map();
  for (const r of rows) {
    const k = keyOf(r);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(r);
  }
  for (const group of byKey.values()) {
    let maxPositive = 0;
    for (const r of group) {
      const n = readSharePercentRaw(r);
      if (n !== null && n > maxPositive) maxPositive = n;
    }
    const fill = maxPositive > 0 ? maxPositive : 1;
    for (const r of group) {
      const n = readSharePercentRaw(r);
      if (n === null || n === 0) {
        r.share_percent = fill;
      }
    }
  }
  return rows;
}

export function getUniqueCropPractices(producerFarmCrop) {
  // Get unique commodity_name, practice_name
  const _producerFarmCropWithGroupKey = [...producerFarmCrop];
  _producerFarmCropWithGroupKey.forEach((current) => {
    current.yield_weight = (current.yield || null) * current.reported_acreage;
    current.rate_yield_weight = (current.rate_yield || null) * current.reported_acreage;
    current.adjusted_yield_weight = (current.adjusted_yield || null) * current.reported_acreage;
    current.share_percent_weight = (current.share_percent || null) * current.reported_acreage;
    current.production_cost_per_acre_weight = current.production_cost_per_acre * current.reported_acreage || null;

    current._GROUP_KEY = JSON.stringify({
      commodity_name: current.commodity_name,
      // type_name: current.type_name,
      practice_name: current.practice_name,
    });
  });

  const _cropTypePracticeList = _(_producerFarmCropWithGroupKey)
    .groupBy("_GROUP_KEY")
    .map((currentGroup, id) => ({
      // Deserialize GROUP_KEY id object props:
      ...JSON.parse(id),

      // Aggregates:
      reported_acreage: _.toNumber(
        _.toNumber(_.sumBy(currentGroup, (item) => Number(item.reported_acreage) || 0))?.toFixed(2)
      ),
      yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      // Child elements:
      unit_count: currentGroup.length,
      // Child elements:
      // eslint-disable-next-line no-unused-vars
      children: currentGroup.map(({ _GROUP_KEY, approved_yield_weight, ...pfcProps }) => pfcProps),
    }))
    .value();

  return _cropTypePracticeList;
}

export function getUniqueCounties(producerFarmCrop) {
  const _producerFarmCropWithGroupKey = [...producerFarmCrop];
  _producerFarmCropWithGroupKey.forEach((current) => {
    current.approved_yield_weight = (current.approved_yield || null) * current.reported_acreage;
    current.rate_yield_weight = (current.rate_yield || null) * current.reported_acreage;
    current.adjusted_yield_weight = (current.adjusted_yield || null) * current.reported_acreage;
    current.share_percent_weight = (current.share_percent || null) * current.reported_acreage;
    current.production_cost_per_acre_weight = current.production_cost_per_acre * current.reported_acreage || null;

    // The same county can be stored with differing capitalization, so group case-insensitively.
    current._GROUP_KEY = JSON.stringify({
      location_state_name: current.location_state_name?.toUpperCase(),
      location_county_name: current.location_county_name?.toUpperCase(),
    });
  });

  const _cropTypePracticeCountyList = _(_producerFarmCropWithGroupKey)
    .groupBy("_GROUP_KEY")
    .map((currentGroup) => ({
      // Display the first row's spelling, not the upper-cased grouping key:
      location_state_name: currentGroup[0].location_state_name,
      location_county_name: currentGroup[0].location_county_name,
      // Aggregates:
      reported_acreage: _.toNumber(
        _.toNumber(_.sumBy(currentGroup, (item) => Number(item.reported_acreage) || 0))?.toFixed(2)
      ),
      base_acreage: _.toNumber(_.toNumber(_.sumBy(currentGroup, (item) => Number(item.base_acreage) || 0))?.toFixed(2)),
      production_cost_per_acre:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.production_cost_per_acre_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.production_cost_per_acre_weight) && Number(item.reported_acreage)
                  ? Number(item.reported_acreage)
                  : 0
              )
          )?.toFixed(2)
        ) || null,

      approved_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.approved_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.approved_yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      rate_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.rate_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.rate_yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      adjusted_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.adjusted_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.adjusted_yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      share_percent_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.share_percent_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.share_percent_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      // Planted-acres farm count only — base-only farms still roll into base_acreage.
      unit_count: currentGroup.filter((item) => Number(item.reported_acreage) > 0).length,
      // Child elements:
      // eslint-disable-next-line no-unused-vars
      producerFarmCrops: currentGroup.map(({ _GROUP_KEY, approved_yield_weight, ...pfcProps }) => pfcProps),
    }))
    .value();

  const _sorted = _cropTypePracticeCountyList.sort(
    (a, b) =>
      a?.location_state_name?.localeCompare(b.location_state_name) ||
      a?.location_county_name?.localeCompare(b.location_county_name)
  );

  return _sorted;
}

export function getUniqueProducerFarmCrops(producerFarmCrop) {
  if (!producerFarmCrop) return [];

  const _producerFarmCropWithGroupKey = [...producerFarmCrop];
  _producerFarmCropWithGroupKey.forEach((current) => {
    current.approved_yield_weight = (current.approved_yield || null) * current.reported_acreage;
    current.production_cost_per_acre_weight = current.production_cost_per_acre * current.reported_acreage || null;

    current._GROUP_KEY = JSON.stringify({
      location_state_name: current.location_state_name,
      location_county_name: current.location_county_name,
      fsa_farm_number: current.fsa_farm_number,

      commodity_name: current.commodity_name,
      type_name: current.type_name,
      practice_name: current.practice_name,
    });
  });

  const _cropTypePracticeCountyList = _(_producerFarmCropWithGroupKey)
    .filter(
      (current) => current.fsa_farm_number && current.commodity_name && current.type_name && current.practice_name
    )
    .groupBy("_GROUP_KEY")
    .map((currentGroup, id) => ({
      // Deserialize GROUP_KEY id object props:
      ...JSON.parse(id),
      farm_name: currentGroup[0]?.farm_name,
      // Aggregates:
      reported_acreage: _.toNumber(
        _.toNumber(_.sumBy(currentGroup, (item) => Number(item.reported_acreage) || 0))?.toFixed(2)
      ),
      base_acreage: _.toNumber(_.toNumber(_.sumBy(currentGroup, (item) => Number(item.base_acreage) || 0))?.toFixed(2)),
      production_cost_per_acre:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.production_cost_per_acre_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.production_cost_per_acre_weight) && Number(item.reported_acreage)
                  ? Number(item.reported_acreage)
                  : 0
              )
          )?.toFixed(2)
        ) || null,
      approved_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.approved_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.approved_yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,
      unit_count: currentGroup.length,
      share_percent: _.toNumber(
        _.toNumber(_.meanBy(currentGroup, (item) => Number(item.share_percent) || 0))?.toFixed(2)
      ),
      rate_yield: _.toNumber(_.toNumber(_.meanBy(currentGroup, (item) => Number(item.rate_yield) || 0))?.toFixed(2)),
      adjusted_yield: _.toNumber(
        _.toNumber(_.meanBy(currentGroup, (item) => Number(item.adjusted_yield) || 0))?.toFixed(2)
      ),
      average_yield: _.toNumber(
        _.toNumber(_.meanBy(currentGroup, (item) => Number(item.average_yield) || 0))?.toFixed(2)
      ),
      // Child elements:
      // eslint-disable-next-line no-unused-vars
      producerFarmCrops: currentGroup.map(({ _GROUP_KEY, approved_yield_weight, ...pfcProps }) => pfcProps),
    }))
    .value();

  const _sorted = _cropTypePracticeCountyList.sort(
    (a, b) =>
      a?.location_state_name?.localeCompare(b.location_state_name) ||
      a?.location_county_name?.localeCompare(b.location_county_name) ||
      a?.fsa_farm_number?.localeCompare(b.fsa_farm_number)
  );

  return _sorted;
}

export function getUniqueFields(producerFarmCrop) {
  const _producerFarmCropWithGroupKey = [...producerFarmCrop];
  _producerFarmCropWithGroupKey.forEach((current) => {
    current.approved_yield_weight = (current.approved_yield || null) * current.reported_acreage;
    current.production_cost_per_acre_weight = current.production_cost_per_acre * current.reported_acreage || null;

    current._GROUP_KEY = JSON.stringify({
      location_state_name: current.location_state_name,
      location_county_name: current.location_county_name,
      fsa_farm_number: current.fsa_farm_number,
      fsa_tract_number: current.fsa_tract_number,
      fsa_field_number: current.fsa_field_number,
    });
  });

  const _cropTypePracticeCountyList = _(_producerFarmCropWithGroupKey)
    .filter((current) => current.fsa_farm_number && current.fsa_tract_number && current.fsa_field_number)
    .groupBy("_GROUP_KEY")
    .map((currentGroup, id) => ({
      // Deserialize GROUP_KEY id object props:
      ...JSON.parse(id),
      // Aggregates:
      reported_acreage: _.toNumber(
        _.toNumber(_.sumBy(currentGroup, (item) => Number(item.reported_acreage) || 0))?.toFixed(2)
      ),
      base_acreage: _.toNumber(_.toNumber(_.sumBy(currentGroup, (item) => Number(item.base_acreage) || 0))?.toFixed(2)),
      production_cost_per_acre:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.production_cost_per_acre_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.production_cost_per_acre_weight) && Number(item.reported_acreage)
                  ? Number(item.reported_acreage)
                  : 0
              )
          )?.toFixed(2)
        ) || null,
      approved_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.approved_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.approved_yield_weight) && Number(item.reported_acreage) ? Number(item.reported_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,
      unit_count: currentGroup.length,
      // Child elements:
      // eslint-disable-next-line no-unused-vars
      producerFarmCrops: currentGroup.map(({ _GROUP_KEY, approved_yield_weight, ...pfcProps }) => pfcProps),
    }))
    .value();

  const _sorted = _cropTypePracticeCountyList.sort(
    (a, b) =>
      a?.location_state_name?.localeCompare(b.location_state_name) ||
      a?.location_county_name?.localeCompare(b.location_county_name) ||
      a?.fsa_farm_number?.localeCompare(b.fsa_farm_number) ||
      a?.fsa_tract_number?.localeCompare(b.fsa_tract_number) ||
      a?.fsa_field_number?.localeCompare(b.fsa_field_number)
  );

  return _sorted;
}

export function getUniqueBaseAcreCrops(baseAcres) {
  const _baseAcresWithGroupKey = [...baseAcres];
  _baseAcresWithGroupKey.forEach((current) => {
    current.plc_yield_weight = (current.plc_yield || null) * current.base_acreage;
    current.share_percent_weight = (current.share_percent || null) * current.base_acreage;

    current._GROUP_KEY = JSON.stringify({
      crop_year: current.crop_year,
      location_state_name: current.location_state_name,
      location_county_name: current.location_county_name,
      commodity_name: current.commodity_name,
      type_name: current.type_name,
    });
  });

  const _cropTypePracticeCountyList = _(_baseAcresWithGroupKey)
    .groupBy("_GROUP_KEY")
    .map((currentGroup, id) => ({
      // Deserialize GROUP_KEY id object props:
      ...JSON.parse(id),
      id: currentGroup[0]?.id,
      // Aggregates:
      base_acreage: _.toNumber(_.toNumber(_.sumBy(currentGroup, (item) => Number(item.base_acreage) || 0))?.toFixed(2)),

      plc_yield_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.plc_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.plc_yield_weight) && Number(item.base_acreage) ? Number(item.base_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,
      plc_yield:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.plc_yield_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.plc_yield_weight) && Number(item.base_acreage) ? Number(item.base_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      share_percent_weighted:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.share_percent_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.share_percent_weight) && Number(item.base_acreage) ? Number(item.base_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,
      share_percent:
        _.toNumber(
          _.toNumber(
            _.sumBy(currentGroup, (item) => Number(item.share_percent_weight) || 0) /
              _.sumBy(currentGroup, (item) =>
                Number(item.share_percent_weight) && Number(item.base_acreage) ? Number(item.base_acreage) : 0
              )
          )?.toFixed(2)
        ) || null,

      unit_count: currentGroup.length,

      // Child elements:
      // eslint-disable-next-line no-unused-vars
      baseAcres: currentGroup.map(({ _GROUP_KEY, plc_yield_weight, ...pfcProps }) => pfcProps),
    }))
    .value();

  const _sorted = _cropTypePracticeCountyList
    .filter((cp) => cp?.base_acreage > 0)
    .sort(
      (a, b) =>
        a?.location_state_name?.localeCompare(b.location_state_name) ||
        a?.location_county_name?.localeCompare(b.location_county_name) ||
        a?.commodity_name?.localeCompare(b.commodity_name)
    );

  return _sorted;
}

export function getUniqueCountiesForHistory(producerFarmCrop) {
  const _producerFarmCropWithGroupKey = [...producerFarmCrop];
  _producerFarmCropWithGroupKey.forEach((current) => {
    // The same county can be stored with differing capitalization, so group case-insensitively.
    current._GROUP_KEY = JSON.stringify({
      location_state_name: current.location_state_name?.toUpperCase(),
      location_county_name: current.location_county_name?.toUpperCase(),
    });
  });

  const _cropTypePracticeCountyList = _(_producerFarmCropWithGroupKey)
    .groupBy("_GROUP_KEY")
    .map((currentGroup) => {
      // 1. Group by Farm Number
      const farmsGrouped = _(currentGroup)
        .groupBy("fsa_farm_number")
        .map((farmGroup, farmId) => {
          // Per field: SUM same commodity_name, then MAX across commodities.
          // Keep in sync with ProducerFarm_Search (vault homepage farm acres).
          const fieldMaxes = _(farmGroup)
            .groupBy((item) =>
              JSON.stringify({
                fsa_tract_number: item.fsa_tract_number,
                fsa_field_number: item.fsa_field_number,
              })
            )
            .map((fieldGroup) => {
              const commoditySums = _(fieldGroup)
                .groupBy((item) => item.commodity_name || item.commodity_code)
                .map((cropGroup) => ({
                  ...cropGroup[0],
                  reported_acreage: _.sumBy(cropGroup, (item) => Number(item.reported_acreage) || 0),
                  base_acreage: _.sumBy(cropGroup, (item) => Number(item.base_acreage) || 0),
                }))
                .value();

              const maxReported = _.max(commoditySums.map((item) => Number(item.reported_acreage) || 0)) || 0;
              const maxBase = _.max(commoditySums.map((item) => Number(item.base_acreage) || 0)) || 0;
              const winner =
                commoditySums.find((item) => Number(item.reported_acreage) === maxReported) || commoditySums[0];

              return {
                ...winner,
                reported_acreage: maxReported,
                base_acreage: maxBase,
                producerFarmCrops: commoditySums,
              };
            })
            .value();

          return {
            fsa_farm_number: farmId,
            ...farmGroup[0],
            reported_acreage: _.toNumber(
              _.sumBy(fieldMaxes, (item) => Number(item.reported_acreage) || 0).toFixed(2)
            ),
            base_acreage: _.toNumber(_.sumBy(fieldMaxes, (item) => Number(item.base_acreage) || 0).toFixed(2)),
            producerFarmCrops: fieldMaxes,
          };
        })
        .value();

      return {
        // Display the first row's spelling, not the upper-cased grouping key:
        location_state_name: currentGroup[0].location_state_name,
        location_county_name: currentGroup[0].location_county_name,
        // Aggregates for the County:
        reported_acreage: _.toNumber(_.sumBy(farmsGrouped, (item) => Number(item.reported_acreage) || 0).toFixed(2)),
        base_acreage: _.toNumber(_.sumBy(farmsGrouped, (item) => Number(item.base_acreage) || 0).toFixed(2)),
        unit_count: farmsGrouped.length,
        // Child elements (Farms):
        producerFarmCrops: farmsGrouped,
      };
    })
    .value();

  const _sorted = _cropTypePracticeCountyList.sort(
    (a, b) =>
      a?.location_state_name?.localeCompare(b.location_state_name) ||
      a?.location_county_name?.localeCompare(b.location_county_name)
  );

  return _sorted;
}
